/* screens/notifications/NotificationsInbox.jsx — полный инбокс (S11).
   PageHeader «Уведомления» + «Прочитать всё»; фильтры (Все/Непрочитанные/категории);
   список InboxRow с per-item прочесть/удалить; контекст пространства (фикс №10).
   Источник правды — useNotificationsStore (общий с колоколом, фикс №50): markRead/
   delete мгновенно меняют бейдж Bell. Колокол на этой странице скрыт (см. PageHeader).
   Состояния: loading / empty_all / empty_filtered / список. ХЕНДОФФ → pages/notifications/NotificationsInbox.jsx. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { EmptyState, ErrorState, ForbiddenState } from '../../components/patterns/States.jsx';
import { InboxRow } from '../../components/patterns/notifications/InboxRow.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { FilterChip } from '../../components/ui/Chip.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { useNotificationsStore } from '../../hooks/useNotificationsStore.jsx';
import { RR } from '../../lib/router.jsx';

  const { useMemo } = React;
  const { useSearchParams, useNavigate } = RR;

  // категория уведомления по type (для фильтров)
  const CATEGORY = {
    invitation_received: 'invitation',
    payment_succeeded: 'payment', payment_failed: 'payment',
    subscription_expiring: 'subscription', subscription_expired: 'subscription',
    gift_expiring: 'subscription',
    // история: прежние окна «за 7 дней» / «за 1 день» остались в инбоксе
    subscription_expiring_7d: 'subscription', subscription_expiring_1d: 'subscription',
    subscription_expiring_early: 'subscription',
    report_ready: 'report',
  };
  const CAT_CHIPS = [
    { id: 'invitation', label: 'Приглашение' },
    { id: 'payment', label: 'Оплата' },
    { id: 'subscription', label: 'Подписка' },
    { id: 'report', label: 'Отчёт' },
  ];

  function NotificationsInbox() {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const store = useNotificationsStore();
    const auth = useAuth();
    const showWorkspace = (auth && auth.workspaces && auth.workspaces.length > 1);

    const filter = params.get('filter') || 'all';
    const setFilter = (f) => setParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (f && f !== 'all') sp.set('filter', f); else sp.delete('filter');
      return sp;
    });

    const all = store.notifications;
    const filtered = useMemo(() => {
      if (filter === 'all') return all;
      if (filter === 'unread') return all.filter((n) => !n.is_read);
      return all.filter((n) => CATEGORY[n.type] === filter);
    }, [all, filter]);

    const openItem = (n) => {
      store.markRead(n.id);
      const dest = n.data && n.data.destination;
      if (dest) navigate(dest);
    };

    // подсчёты для чипов
    const counts = useMemo(() => {
      const c = { all: all.length, unread: all.filter((n) => !n.is_read).length };
      CAT_CHIPS.forEach((ch) => { c[ch.id] = all.filter((n) => CATEGORY[n.type] === ch.id).length; });
      return c;
    }, [all]);

    const actions = (
      <Button variant="ghost" size="sm" iconLeft="check" disabled={counts.unread === 0}
              onClick={() => store.markAllRead()}>Прочитать всё</Button>
    );

    let body;
    if (store.error && store.error.status === 403) {
      body = <ForbiddenState />;
    } else if (store.error) {
      body = <ErrorState onRetry={() => store.refetch()} />;
    } else if (store.loading) {
      body = (
        <div className="inbox-list">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="inbox-row inbox-row--skeleton">
              <span className="inbox-row__icon skeleton-box" />
              <div className="inbox-row__main">
                <span className="skeleton-line" style={{ width: '55%' }} />
                <span className="skeleton-line" style={{ width: '80%' }} />
              </div>
            </div>
          ))}
        </div>
      );
    } else if (filtered.length === 0) {
      body = filter === 'all'
        ? <EmptyState icon="notifications" title="Уведомлений пока нет"
            body="Здесь появятся события: оплаты, приглашения, готовые отчёты и инвентаризации." />
        : <EmptyState icon="notifications" serif={false} title="Нет уведомлений в этой категории"
            body="Попробуйте другой фильтр или сбросьте текущий."
            action={<Button variant="secondary" onClick={() => setFilter('all')}>Показать все</Button>} />;
    } else {
      body = (
        <div className="inbox-list">
          {filtered.map((n) => (
            <InboxRow key={n.id} notification={n} showWorkspace={showWorkspace}
              onToggleRead={store.toggleRead} onDelete={store.deleteNotification} onOpen={openItem} />
          ))}
        </div>
      );
    }

    return (
      <div className="section-page">
        <PageHeader title="Уведомления" subtitle="Центр событий" actions={actions} />
        <div className="section-body notif-page">
          <div className="notif-page__inner">
            <div className="notif-filters scroll-row" role="group" aria-label="Фильтры уведомлений">
              <FilterChip active={filter === 'all'} onToggle={() => setFilter('all')} count={counts.all}>Все</FilterChip>
              <FilterChip active={filter === 'unread'} onToggle={() => setFilter('unread')} count={counts.unread}>Непрочитанные</FilterChip>
              <span className="notif-filters__sep" aria-hidden="true" />
              {CAT_CHIPS.map((ch) => (
                <FilterChip key={ch.id} active={filter === ch.id} onToggle={() => setFilter(ch.id)} count={counts[ch.id]}>{ch.label}</FilterChip>
              ))}
            </div>
            {body}
          </div>
        </div>
      </div>
    );
  }

export { NotificationsInbox };
// Для теста: категория фильтра обязана быть у каждого типа подписки (lib/adapters/notificationsPrefs.test.js).
export { CATEGORY as NOTIF_CATEGORY };
